import React, { useState, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import { giphyCharcoal, giphyIndigo } from '@giphy/js-brand'

const TRACK_WIDTH = 345
const TRACK_HEIGHT = 33

const HANDLE_WIDTH = 14
const HANDLE_HEIGHT = 40

const Track = styled.div`
    position: relative;
    background-color: ${giphyCharcoal};
    width: ${TRACK_WIDTH}px;
    height: ${TRACK_HEIGHT}px;
`

const Label = styled.div`
    display: none;
    background-color: ${giphyIndigo};
    position: absolute;
    bottom: -40px;
    padding: 5px;
    border-radius: 4px;
    text-align: center;

    &:after {
        content: '';
        position: absolute;
        left: 10px;
        top: -4px;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        border-bottom: 4px solid ${giphyIndigo};
    }
`

const LabelText = styled.div`
    font-size: 12px;
    font-weight: bold;
    line-height: 14px;
    letter-spacing: 0.26px;
`

const Handle = styled.div<{ value }>`
    width: ${HANDLE_WIDTH}px;
    height: ${HANDLE_HEIGHT}px;
    position: absolute;
    top: -10px;
    background-image: url('/static/img/svg/grabber.svg');
    background-position: -4px -2px;
`

const Handle1 = styled(Handle)`
    transform: translate3d(0, 0, 0);

    &:hover {
        ${Label} {
            display: block;
        }
    }
`

const Handle2 = styled(Handle)`
    transform: translate3d(0, 0, 0) scaleX(-1);

    ${LabelText} {
        transform: translate3d(0, 0, 0) scaleX(-1);
    }

    &:hover {
        ${Label} {
            display: block;
        }
    }
`

const selectedRangeAnimation = keyframes`
    0% {
        transform: translate3d(0, 0, 0);
    }
    100% {
        transform: translate3d(-29px, 0, 0);
    }
`

const SelectedRange = styled.div`
    position: absolute;
    height: 100%;
    background-color: ${giphyIndigo};
    overflow: hidden;
    &:after {
        animation: ${selectedRangeAnimation} 0.75s linear 0s infinite;
        background-image: repeating-linear-gradient(
            135deg,
            rgba(0, 0, 0, 0),
            rgba(0, 0, 0, 0) 10px,
            rgba(0, 0, 0, 0.1) 10px,
            rgba(0, 0, 0, 0.1) 20px
        );
        box-sizing: content-box;
        content: '';
        height: 100%;
        left: 0;
        padding-right: 30px;
        position: absolute;
        top: 0;
        width: 100%;
    }
`

type Props = {
    min: number
    max: number
    value1: number
    value2: number
    label1?: string
    label2?: string
    minDiff?: number
    onValue1Changed?: (value: number) => void
    onValue2Changed?: (value: number) => void
}

const RangeSlider = ({
    min,
    max,
    value1,
    value2,
    label1,
    label2,
    minDiff,
    onValue1Changed = () => {},
    onValue2Changed = () => {},
}: Props) => {
    const [isDraggingHandle1, setIsDraggingHandle1] = useState(false)
    const [isDraggingHandle2, setIsDraggingHandle2] = useState(false)
    const [previousDragX, setPreviousDragX] = useState(-1)

    useEffect(() => {
        if (isDraggingHandle1 || isDraggingHandle2) {
            startMouseTracking()
        }

        return () => stopMouseTracking()
    }, [isDraggingHandle1, isDraggingHandle2])

    const startMouseTracking = () => {
        window.addEventListener('mousemove', onMouseMove, false)
        window.addEventListener('mouseup', onMouseUp, false)
    }

    const stopMouseTracking = () => {
        window.removeEventListener('mousemove', onMouseMove, false)
        window.removeEventListener('mouseup', onMouseUp, false)
    }

    const onMouseUp = () => {
        if (isDraggingHandle1) {
            setIsDraggingHandle1(false)
        }
        if (isDraggingHandle2) {
            setIsDraggingHandle2(false)
        }
        stopMouseTracking()
    }

    const onMouseMove = evt => {
        const xChange = evt.screenX - previousDragX
        const percentChange = xChange / TRACK_WIDTH
        const valueChange = max * percentChange

        setPreviousDragX(evt.screenX)

        if (isDraggingHandle1) {
            const newValue1 = value1 + valueChange
            const aboveMinDiff = minDiff ? value2 - newValue1 >= minDiff : true
            if (newValue1 > min && newValue1 < value2 && aboveMinDiff) {
                onValue1Changed(newValue1)
            }
        }

        if (isDraggingHandle2) {
            const newValue2 = value2 + valueChange
            const aboveMinDiff = minDiff ? newValue2 - value1 >= minDiff : true
            if (newValue2 < max && newValue2 > value1 && aboveMinDiff) {
                onValue2Changed(newValue2)
            }
        }
    }

    const onHandle1MouseDown = evt => {
        setPreviousDragX(evt.screenX)
        setIsDraggingHandle1(true)
    }

    const onHandle2MouseDown = evt => {
        setPreviousDragX(evt.screenX)
        setIsDraggingHandle2(true)
    }

    const value1Pct = value1 / max
    const value2Pct = value2 / max

    return (
        <Track>
            <SelectedRange
                style={{
                    left: `${value1Pct * TRACK_WIDTH}px`,
                    width: `${(value2Pct - value1Pct) * TRACK_WIDTH}px`,
                }}
            />
            <Handle1
                value={value1Pct}
                onMouseDown={onHandle1MouseDown}
                style={{ left: `${value1Pct * TRACK_WIDTH - HANDLE_WIDTH}px` }}
            >
                {label1 && (
                    <Label>
                        <LabelText>{label1}</LabelText>
                    </Label>
                )}
            </Handle1>
            <Handle2
                value={value2Pct}
                onMouseDown={onHandle2MouseDown}
                style={{ left: `${value2Pct * TRACK_WIDTH}px` }}
            >
                {label2 && (
                    <Label>
                        <LabelText>{label2}</LabelText>
                    </Label>
                )}
            </Handle2>
        </Track>
    )
}

export default RangeSlider
